/**
 * Centralized frontend error handling.
 *
 * One entry point — `handleError(error, ctx)` — for every `catch` block in the app.
 * It does three things, in order:
 *   1. Classifies the error (access / not-found / conflict / validation / network /
 *      offline / server / rate-limited / expected / unknown) from the existing axios
 *      type guards.
 *   2. Reports it to Sentry exactly once (deduped across the global React Query net
 *      in `AppProviders.tsx` via a shared WeakSet).
 *   3. Shows UX — a typed handler (`onAccessError` / `onNotFound` / `onNetworkError`)
 *      if one matches, otherwise a toast (unless `silent`).
 *
 * Reporting is exactly-once because React Query's cache `onError` runs *before* a
 * `mutateAsync` catch resolves: by the time `handleError` runs, an RQ error is already
 * in the WeakSet, so `handleError` only adds UX. Non-RQ errors (raw fetch, JSON.parse,
 * clipboard, streams) are reported by `handleError` itself. Verified against real
 * React Query rather than inferred: a `MutationObserver` whose rejection the caller
 * catches runs `cache-onError` first, then the caller's `catch`.
 *
 * 🚨 **SO ON A REACT-QUERY PATH, THE `component` AND `operation` YOU PASS HERE NEVER
 * REACH SENTRY.** The global net won the WeakSet, and the event it already sent is
 * fingerprinted `source: "mutation" | "query"` + its query/mutation key. The
 * generated NexusApi hooks set no `mutationKey`, so that key is literally
 * `"unknown"`. Adding `handleError` to a `catch` around a `mutateAsync` therefore
 * changes the UX and changes NOTHING about what Sentry receives — it is not
 * "restoring a missing report", and two separate passes over the blind-catch class
 * described it as one.
 *
 * The half that genuinely has no other reporter is everything the net cannot see:
 * the imperative `api.<domain>.<method>()` methods on the generated client (raw
 * axios, no React Query), plus clipboard, `JSON.parse`, ProseMirror, serializers and
 * streams. Know which half a site is on before claiming what a report bought.
 *
 * @see AppProviders.tsx — the automatic global net (Layer 1)
 * @see api-errors.ts — the axios status type guards this builds on
 */

import { CAPABILITY_UNAVAILABLE, CapabilityUnavailableDetailsSchema } from "@nexus/types"
import { FEATURE_ACCESS_RESTRICTED_CODE } from "@nexus/types/domain"
import * as Sentry from "@sentry/react"
import { isAxiosError } from "axios"
import i18next from "i18next"
import { useCallback } from "react"
import { ZodError } from "zod"

import { getOAuthReconnectError } from "@/common/api/oauth-error-interceptor"
import { readSessionSignal, type SessionSignal } from "@/common/auth/session-signal"
// The MODULE, never `@/common/capabilities` — that barrel re-exports
// `CapabilityGuard`, which imports `@agent-nexus/ui`, so the shortest spelling
// would pull the design system into every consumer of this module and would put
// a real runtime edge back into a graph whose only remaining one is type-only.
import { capabilityOutageCopy } from "@/common/capabilities/capability-outage-copy"
import { toast } from "@/common/components/UI/Toast"
import {
  explainsRestriction,
  getFeatureRestriction
} from "@/common/feature-access/feature-restriction"
import { featureRestrictionMessage } from "@/common/feature-access/feature-restriction-copy"
import { ErrorCode, ErrorType, getErrorMessage } from "@/i18n/errors"

import {
  getErrorData,
  getErrorMessage as getAxiosErrorMessage,
  getErrorStatus,
  serverErrorCode,
  serverErrorDetails,
  serverErrorMessage
} from "./api-errors"

/**
 * Discriminated error kind. Drives both the user message and the reporting policy.
 *
 * Reporting policy: only "something is actually broken" reaches Sentry — `server`,
 * `network`, `validation` (a 4xx the client shouldn't have sent), and `unknown`.
 * Expected client outcomes the app handles via UX — `notFound` (404), `conflict` (409),
 * `offline`, `capabilityUnavailable` (a feature is off), and `expected` (OAuth reconnect /
 * aborted) — still toast or route to a typed handler, but never create a Sentry event.
 * This keeps Sentry signal high and the system maintenance-free.
 *
 * `access` is the one kind the status alone cannot decide: see {@link isReportable},
 * which resolves it against the session as well.
 */
export type ErrorKind =
  | "access" // 401 / 403 — not signed in or not permitted (401 reported only when signed in)
  | "notFound" // 404 — expected, not reported
  | "conflict" // 409 (non-OAuth) — control flow, not reported
  | "validation" // 400 / 422 / ZodError — usually a client contract bug, reported
  | "network" // request made, no response (server unreachable) — reported
  | "offline" // browser is offline — user-side, not reported
  | "server" // 5xx — reported
  | "rateLimited" // 429 — reported, exactly as `server` was. See REPORT_POLICY.
  | "capabilityUnavailable" // CAPABILITY_UNAVAILABLE — a gated feature is off, not reported
  | "expected" // OAuth reconnect signal, aborted/cancelled request — not reported
  | "unknown" // reported

/**
 * Whether a kind reaches Sentry. `whenAuthenticated` is the one conditional policy —
 * see {@link isReportable} for the pair it resolves against.
 *
 * An exhaustive `Record` rather than a `Set`: adding an `ErrorKind` with no policy is a
 * compile error, where a missing `Set` member would silently mean "report it".
 */
type ReportPolicy = "always" | "never" | "whenAuthenticated"

const REPORT_POLICY: Record<ErrorKind, ReportPolicy> = {
  access: "whenAuthenticated",
  notFound: "never",
  conflict: "never",
  offline: "never",
  expected: "never",
  // ══════════════════════════════════════════════════════════════════════════
  // 🔴 `never`, AND THE ARGUMENT IS A RATE, NOT A PREFERENCE.
  // ══════════════════════════════════════════════════════════════════════════
  //
  // `reportError` dedupes on the error OBJECT's identity through a `WeakSet`,
  // and every axios rejection is a fresh object. So a capability down behind a
  // `refetchInterval` produces one Sentry event PER POLL, for as long as the
  // outage lasts — a flood whose size is a function of the polling interval and
  // nothing else. `access`'s `whenAuthenticated` arm exists because a 401 burst
  // carried real signal; this one carries none.
  //
  // And there is nothing to learn from it. An outage is known to operators by
  // construction: `_common/capabilities/log-capabilities-on-boot.service.ts`
  // names every non-ready row at boot, `GET /api/capabilities` serves the same
  // reading on demand, and `_common/capabilities/alerting/` ships a Sentry sink
  // and a Slack sink beside `report-degraded-capabilities.service.ts`. A second
  // Sentry event per affected user adds users, never facts.
  //
  // ⚠️ THE FIX THIS ENTRY IS NOT: keying the dedupe on the capability NAME
  // rather than on object identity would make `always` affordable, and it is a
  // change to `reportError`'s contract for every kind. Not made here.
  capabilityUnavailable: "never",
  validation: "always",
  network: "always",
  server: "always",
  // 429 reported exactly as it was while it lived inside `server`. Splitting the
  // kind changed the user-facing MESSAGE and deliberately changed nothing about
  // reportability: grouping 429 with 5xx for Sentry is a declared, defensible
  // call (the `ErrorKind` comment states it), whereas inheriting
  // `internal_server_error` as the user's message was an undeclared consequence
  // of that grouping. Silencing 429 is a separate decision with its own
  // evidence, not a free rider on this one.
  rateLimited: "always",
  unknown: "always"
}

/**
 * Errors already sent to Sentry. Shared with the global React Query net so an error
 * is reported exactly once regardless of which layer sees it first. A `WeakSet` keyed
 * on the error reference is robust to frozen errors and never serializes into payloads.
 */
const reportedErrors = new WeakSet<object>()

/** The result of classifying an error: its kind plus a user-ready message. */
export interface ClassifiedError {
  kind: ErrorKind
  /** HTTP status when the error came from the API, otherwise `undefined`. */
  status?: number
  /** Localized, user-safe message suitable for a toast. */
  userMessage: string
}

/**
 * Decides whether a classified error reaches Sentry, from the pair `(status, session)` —
 * never from the status alone.
 *
 * A 401 means two different things and the status cannot tell them apart:
 *   - no session — "you are signed out". Expected, high volume, never reported.
 *   - a LIVE session — the server refused a caller it had already authenticated. That is
 *     always a defect somewhere, and suppressing it is what hid a nine-day production
 *     failure whose only frontend trace was three unattributed events from Sentry's own
 *     global unhandled-rejection handler.
 *
 * `unknown` counts as reportable. An uninstalled probe must fail TOWARDS telemetry: if it
 * resolved to "no session" the behaviour would be byte-identical to the bug above, and
 * nothing would ever go red. `reportError` tags the event with the signal, so an unwired
 * probe surfaces as a burst of `session: unknown` events rather than as silence.
 *
 * 403 stays non-reportable at every session state. It is a permission decision about a
 * caller the server DID recognise, and production serves them in bulk — `backend-prd`
 * carried ~88,000 `ForbiddenException` events in the 90 days to 2026-08-09, against
 * ~5,100 frontend events in total over the same window. Reporting those would bury the
 * signal this restores.
 *
 * Pure — no Sentry, and no session read of its own. The caller supplies the session.
 */
export function isReportable(classified: ClassifiedError, session: SessionSignal): boolean {
  switch (REPORT_POLICY[classified.kind]) {
    case "never":
      return false
    case "always":
      return true
    case "whenAuthenticated":
      return classified.status === 401 && session !== "anonymous"
  }
}

/**
 * Context every `handleError` call must provide, plus optional UX overrides.
 *
 * **There is deliberately no `expected` / `silentReport` / `dontReport` option, and
 * none may be added.** Whether an error reaches Sentry is decided by
 * {@link REPORT_POLICY} from the error ITSELF, never by the caller's belief about
 * it. A caller-side override would move that decision to the moment an author is
 * least able to make it — writing a `catch`, before knowing everything that can
 * reach it — and it fails silently in the expensive direction: a real defect marked
 * expected disappears, and nothing ever goes red.
 *
 * `silent` is not that option. It suppresses the TOAST and still reports.
 *
 * So there are exactly two ways to make a failure not report, and both are
 * reviewable:
 *
 * 1. **The failure genuinely is a kind nothing should report** — add an
 *    {@link ErrorKind} and its {@link REPORT_POLICY} entry. One place, exhaustively
 *    typed (a kind with no policy is a compile error), reviewed once, and it then
 *    applies everywhere that error shape occurs rather than at the one call site
 *    that remembered.
 * 2. **This ONE site is expected and the classifier cannot know it** — a user
 *    cancelling a clipboard consent dialog, a `fetch` to a URL the user just typed.
 *    Do not call `handleError`. Toast, and carry
 *    `eslint-disable-next-line nexus/report-caught-errors -- <reason>`, which the
 *    lint rule requires and which `check-lint-debt` retires automatically once the
 *    directive stops suppressing anything.
 *
 * Option 2 costs a visible, greppable, countable line. That cost is the feature: it
 * is what an `expected: true` flag would have removed.
 *
 * **DEPTH IS NOT FREE — adding a report to an INNER frame moves an existing Sentry
 * issue rather than creating a second one.** {@link reportError} dedupes on the
 * error REFERENCE, and in nested frames the inner `catch` runs first. So when an
 * error already reported by an outer frame gains a report at an inner one, the
 * inner call wins the `WeakSet`, the outer `captureException` is skipped, and the
 * fingerprint — which carries `source` and `operation` — becomes the INNER frame's.
 * Every event silently re-groups under a new issue and the old one goes quiet. That
 * reads exactly like a bug being fixed.
 *
 * So "this error is not reported" is a claim about a CALL PATH, never about one
 * `catch`. Before adding a report, check whether a frame above already reports the
 * same error object; if one does, that is where the report belongs. This is the
 * second reason a caller-side `expected` flag would be wrong — it answers "should
 * this report?" when the live question is "at which depth?", and it gets both
 * possible answers wrong: on a frame that swallows, the error then reports nowhere;
 * on a frame that rethrows, it hands the fingerprint to whichever frame is next.
 */
export interface HandleErrorContext {
  /** Where the error happened (component / hook / service name). */
  component: string
  /** What was being attempted (e.g. `"createGoogleSheet"`). */
  operation: string
  /** Suppress the toast (still reports to Sentry). */
  silent?: boolean
  /** Override the auto-derived user message shown in the toast. */
  toastMessage?: string
  /** Extra structured context attached to the Sentry event. */
  extra?: Record<string, unknown>
  /** Called for 401/403 — caller owns the UX, the toast is suppressed. */
  onAccessError?: () => void
  /** Called for network/offline errors — caller owns the UX, the toast is suppressed. */
  onNetworkError?: () => void
  /** Called for 404 — caller owns the UX, the toast is suppressed. */
  onNotFound?: () => void
  /**
   * Called when a gated feature is off — caller owns the UX, the toast is suppressed.
   *
   * 🔑 THE ONLY TYPED HANDLER THAT TAKES AN ARGUMENT, BECAUSE THE NAME IS THE
   * POINT. Its siblings describe a condition the caller already knows it asked
   * about; this one carries WHICH capability refused, which is what selects the
   * label a user reads. `capability` is `null` when the server named one this
   * build does not recognise — still an outage, just an unnamed one.
   *
   * Wiring this is what turns a toast into a dead region. A toast is the correct
   * FLOOR and is what every unwired site gets; it is the wrong ceiling, because
   * a toast dismisses and a capability outage does not — `feedback-and-states.md`
   * routes a persistent condition to a banner or an inline notice, and
   * `component-banner.md` forbids a banner inside `NxDialog`, so in a dialog the
   * shape is the body-copy recipe. `ConnectGoogleSheetModal` is the worked example.
   */
  onCapabilityUnavailable?: (outage: CapabilityOutage) => void
}

/** Narrows an unknown value to a non-null object (safe to use as a WeakSet key). */
function isObject(value: unknown): value is object {
  return typeof value === "object" && value !== null
}

/** True when the browser reports itself offline (guarded for SSR/edge). */
function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false
}

/** True for aborted/cancelled requests — never user-actionable, never reported. */
function isAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") return true
  if (isAxiosError(error) && (error.code === "ERR_CANCELED" || error.name === "CanceledError"))
    return true
  return false
}

/**
 * Seconds to wait, read from the `retry-after` response header, or `undefined`
 * when the server did not send a usable one.
 *
 * 🚨 **THE ABSENT CASE IS THE COMMON CASE, AND IT IS THE ONE THAT SHIPS BROKEN.**
 * A `retry-after` header is not guaranteed on a 429, so anything rendering
 * {@link ErrorCode.RATE_LIMIT_EXCEEDED} must have a value for its `{retryAfter}`
 * placeholder or pick the placeholder-free entry instead — see
 * {@link rateLimitMessage}.
 *
 * RFC 9110 permits the header to be an HTTP-date as well as a delay in seconds.
 * `Number("Wed, 21 Oct 2015 07:28:00 GMT")` is `NaN`, so a date-form header
 * resolves to `undefined` here and falls to the placeholder-free message. That is
 * the correct outcome rather than a gap: a wrong number is worse than no number,
 * and no caller in this app sends the date form.
 *
 * The reader is `detectOAuthError.ts`'s, lifted out of its OAuth-only branch so
 * the generic path can see the header too.
 */
function readRetryAfterSeconds(error: unknown): number | undefined {
  if (!isAxiosError(error)) return undefined

  const raw = error.response?.headers?.["retry-after"]
  if (typeof raw !== "string" && typeof raw !== "number") return undefined

  const seconds = Number(raw)
  if (!Number.isFinite(seconds) || seconds <= 0) return undefined

  return Math.ceil(seconds)
}

/**
 * The user-facing message for a 429, which is the ONLY kind whose message depends
 * on a response header rather than on the body alone.
 *
 * Two catalog entries, because one of them cannot render safely without a value:
 * `rate_limit_exceeded` names the wait and needs `{retryAfter}`;
 * `rate_limit_exceeded_unknown_delay` says the same thing without a number.
 * {@link interpolateParams} is `if (!params) return message`, so choosing the
 * first without the header would print the literal `{retryAfter}` to a user.
 */
function rateLimitMessage(error: unknown, backendMessage: string | undefined): string {
  const retryAfter = readRetryAfterSeconds(error)

  if (retryAfter === undefined) {
    return getErrorMessage(
      ErrorType.RATE_LIMIT_ERROR,
      ErrorCode.RATE_LIMIT_EXCEEDED_UNKNOWN_DELAY,
      undefined,
      backendMessage,
      "en"
    )
  }

  return getErrorMessage(
    ErrorType.RATE_LIMIT_ERROR,
    ErrorCode.RATE_LIMIT_EXCEEDED,
    { retryAfter },
    backendMessage,
    "en"
  )
}

/**
 * Maps an error kind to a default `(type, code)` for the i18n message catalog.
 *
 * `rateLimited` is deliberately absent: it needs one of TWO pairs depending on
 * whether a `retry-after` header arrived, which a single entry cannot express.
 * {@link rateLimitMessage} owns that choice, and {@link deriveUserMessage} calls
 * it before reaching this map.
 */
const KIND_MESSAGE: Partial<Record<ErrorKind, { type: ErrorType; code: ErrorCode }>> = {
  access: { type: ErrorType.AUTHORIZATION_ERROR, code: ErrorCode.INSUFFICIENT_PERMISSIONS },
  notFound: { type: ErrorType.NOT_FOUND, code: ErrorCode.RESOURCE_NOT_FOUND },
  conflict: { type: ErrorType.CONFLICT, code: ErrorCode.RESOURCE_ALREADY_EXISTS },
  validation: { type: ErrorType.VALIDATION_ERROR, code: ErrorCode.INVALID_VALUE },
  server: { type: ErrorType.INTERNAL_ERROR, code: ErrorCode.INTERNAL_SERVER_ERROR }
}

/**
 * Derives the user-facing message: backend i18n contract first, then sane fallbacks.
 *
 * 🚨 TWO CONTRACTS AT TWO DEPTHS, AND CONFLATING THEM IS THE BUG THIS FUNCTION
 * ALREADY SHIPPED ONCE.
 *
 * · The **canonical envelope** — `{ success, error: { code, message, details? } }`.
 *   Every `DomainHttpError` and every payload naming a SCREAMING_SNAKE code
 *   reaches the client this way, on every route, since NEX-4582. Read its code
 *   through `serverErrorCode` and its message through `serverErrorMessage`, which
 *   are the only sanctioned readers of that depth.
 * · The **i18n contract** — a TOP-LEVEL `(type, code)` pair, lower_snake, beside
 *   `message`. `STRUCTURED_ERROR_CODE` in `http-exception.filter.ts` rejects a
 *   lower_snake code on purpose, so this pair is never enveloped and is still read
 *   at the top level.
 *
 * `contractCode` and `serverErrorCode(error)` are therefore DIFFERENT VALUES, and
 * naming them apart is the whole documentation. Reading one at the other's depth
 * resolves `undefined`, drops through to the generic arm below, and shows a
 * correct-looking sentence about the wrong thing — no throw, no log, no red.
 * `error-handler.envelope-depth.test.ts` pins both depths, including the refusals
 * that stop a future repair "accepting both".
 */
function deriveUserMessage(error: unknown, kind: ErrorKind): string {
  const data = getErrorData(error)
  const type = typeof data?.type === "string" ? data.type : undefined
  const contractCode = typeof data?.code === "string" ? data.code : undefined
  const param = typeof data?.param === "string" ? data.param : undefined
  const backendMessage = serverErrorMessage(error) ?? undefined

  // 1. A refusal whose OWN message is the only thing that helps.
  //
  //    `getErrorMessage` consults the i18n catalog first and falls back to the
  //    backend string only when the catalog has no entry — and the catalog DOES
  //    have one for AUTHORIZATION_ERROR / INSUFFICIENT_PERMISSIONS ("You don't
  //    have permission to perform this action"). So without this branch the
  //    generic sentence wins at step 3 below and the server's message is
  //    discarded, even though it is strictly more useful: it names the feature
  //    and says an organization admin can grant it.
  //
  //    That generic dead end is the whole of NEX-3949 — 9,344 events from 22
  //    users who were shown a failure with no next step and retried. Feature
  //    access is an admin GRANT, not a plan tier, so "try again" and "upgrade"
  //    are both advice that can never come true; asking an admin is the only
  //    thing that resolves it.
  //
  //    When the envelope's `details` say WHICH of the caller's own groups leave
  //    the feature out, the sentence is built from them instead, so the ask can
  //    name the group an admin has to change — and say when the feature simply
  //    arrived after that group was set up. Copy and reader are
  //    `common/feature-access/`; a backend that sends no `restrictedBy` keeps
  //    the server's own sentence, which is never worse than what it had.
  const restriction = getFeatureRestriction(error)
  if (restriction && explainsRestriction(restriction)) {
    return featureRestrictionMessage(restriction, i18next.t, i18next.language ?? "")
  }
  if (serverErrorCode(error) === FEATURE_ACCESS_RESTRICTED_CODE && backendMessage) {
    return backendMessage
  }

  // 2. A capability refusal, whose sentence the backend deliberately does not
  //    carry. Both arms below are wrong for it, and the second is wrong LOUDLY:
  //
  //    · `KIND_MESSAGE` has no entry for this kind and must not gain one —
  //      `KIND_MESSAGE.server` is the live path for essentially every 5xx and
  //      every 429 in the product, so a word changed there is a silent reword
  //      of all of them.
  //    · without this branch it therefore falls to step 5, where
  //      `getAxiosErrorMessage` prefers `serverErrorMessage` — and `message` is
  //      pinned to the CODE STRING by `z.literal`. The toast reads
  //      `CAPABILITY_UNAVAILABLE`. A wire constant, on a customer's screen.
  //
  //    `i18next.t` rather than a `useTranslation` hook: this function is called
  //    from `classifyError`, which is documented pure and SSR-safe and is also
  //    called by the global React Query net, so no hook is reachable. The
  //    default export IS the singleton `src/i18n/index.ts` initialises —
  //    `date.utils.ts` already reads `i18next.language` on that basis — and the
  //    copy module itself stays pure by taking `t` as a parameter, which is the
  //    app's own idiom for translated text resolved outside a component.
  const outage = getCapabilityOutage(error)
  if (outage) {
    return capabilityOutageCopy(outage.capability, i18next.t).body
  }

  // 3. Backend sent the standardized { type, code } contract — translate it.
  if (type && contractCode) {
    return getErrorMessage(type, contractCode, param ? { param } : undefined, backendMessage, "en")
  }

  // 3b. A rate limit, whose message depends on a HEADER and not on the body.
  //     Above the `KIND_MESSAGE` lookup because that map holds one pair per kind
  //     and this kind needs two. Below the `(type, code)` contract so a backend
  //     that does send the pair still wins, exactly as it does for every kind.
  if (kind === "rateLimited") {
    return rateLimitMessage(error, backendMessage)
  }

  // 4. No contract — use the generic message for this kind.
  const fallback = KIND_MESSAGE[kind]
  if (fallback) {
    return getErrorMessage(fallback.type, fallback.code, undefined, backendMessage, "en")
  }

  // 5. Last resort — the server's own sentence, then whatever the error carries.
  //    `getAxiosErrorMessage` consults `serverErrorMessage` first, so it already
  //    prefers the envelope over axios's "Request failed with status code N".
  return getAxiosErrorMessage(error, "Something went wrong. Please try again.")
}

/**
 * Every capability whose refusal this build can put a name to.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🔴 THESE ARE THE BACKEND REGISTRY'S WIRE NAMES. THEY ARE NOT
 *    `common/capabilities`' `CapabilityName`, AND THE TWO SETS ARE DISJOINT.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Two different mechanisms answer "is this feature off", and only one of them
 * speaks over the wire:
 *
 * · `CapabilityGuard` + `localCapabilitySource` read BUILD-TIME client config and
 *   render BEFORE the click. Their names — `console-auth`, `payments`,
 *   `whatsapp-embedded-signup`, … — are a closed union in
 *   `common/capabilities/capability.ts`.
 * · A `@RequiresCapability` route refuses AFTER the click, with a 503 carrying the
 *   SERVER registry's own name. Those names come from
 *   `apps/backend/src/**\/*.capability.ts`.
 *
 * Measured 2026-09-17 at `71320f6c8e`: 5 names on the client side, 27 registered
 * on the server side, **intersection empty** (`comm -12` over both sorted sets,
 * with the same `comm` over the server set against itself printing all 27 as the
 * control). So reusing `CapabilityName` here would have typechecked, compiled,
 * and resolved every real outage to `null`.
 *
 * Re-derive rather than trusting this list — it is a census, and the next gated
 * route invalidates it silently. Both arms, from a product worktree:
 *
 * ```sh
 * git grep -h 'RequiresCapability("' -- 'apps/backend/src' ':!*.spec.ts'   # the gated set
 * git grep -c 'RequiresCapability' -- 'apps/backend/src' | wc -l           # CONTROL, larger
 * ```
 *
 * ⚠️ ONE OF THE THREE IS REACHABLE FROM THIS APP TODAY. `spreadsheet-import`
 * guards `POST /api/documents/upload/google-sheet{,/columns}`, which
 * `Documents/modals/ConnectGoogleSheetModal.tsx` calls through
 * `api.documents.useGoogleSheetColumns()`. The other two guard routes with no
 * generated client and no caller in `apps/frontend/src` — `document-generation`
 * has no entry in the generated client at all, and `support-chat-identity`'s
 * `GET /api/users/me/intercom/user-hash` has no hook in `generated/users.ts`.
 * They are named here because the WIRE can carry them, not because a screen has
 * met one.
 */
export const OUTAGE_CAPABILITY_NAMES = [
  "spreadsheet-import",
  "document-generation",
  "support-chat-identity"
] as const

/** A capability name this build recognises. Key it to map an outage to copy. */
export type OutageCapabilityName = (typeof OUTAGE_CAPABILITY_NAMES)[number]

/** True for a served name this build knows. A guard, so no cast is needed. */
function isOutageCapabilityName(value: string): value is OutageCapabilityName {
  return OUTAGE_CAPABILITY_NAMES.some((known) => known === value)
}

/**
 * A capability refusal, parsed.
 *
 * `capability` is `null` when the server named a capability this build does not
 * recognise — a gated route added since this bundle was built, or a name that
 * arrived malformed. **That is still an outage.** A reader that answered "not an
 * outage" for an unrecognised name would render a real, permanent, deployment-wide
 * refusal as a generic "something went wrong", which is the exact failure this
 * whole surface exists to end.
 */
export interface CapabilityOutage {
  readonly capability: OutageCapabilityName | null
}

/**
 * Returns the parsed refusal if `error` is the backend's `CAPABILITY_UNAVAILABLE`
 * response, otherwise `null` — so the `null` IS the predicate, exactly as
 * `getOAuthReconnectError` is used.
 *
 * ── WHY THE CODE IS THE ONLY DISCRIMINATOR ───────────────────────────────────
 *
 * The code comes through {@link serverErrorCode} and nothing else. Two cheaper
 * spellings are available here and both are bugs:
 *
 * · **The STATUS.** `RequiresCapabilityGuard` throws
 *   `ServiceUnavailableException`, so the refusal is a 503 today — and a 503 is
 *   also every unrelated outage, every proxy hiccup and every cold start. Adding
 *   `status === 503` as a second condition cannot make a true positive truer; it
 *   can only make the reader MISS a real refusal the day a gateway rewrites the
 *   status. The code is the contract; the status is a coincidence that currently
 *   agrees with it.
 * · **The MESSAGE.** `message` is pinned to the code string by `z.literal`, so
 *   matching on it works right now and is a trap: the backend carries no display
 *   copy precisely so the sentence can change without a contract change. This
 *   reader must not be the thing that stops it changing.
 *
 * ── WHY THE COPY IS NOT HERE ─────────────────────────────────────────────────
 *
 * Nothing human-readable crosses this boundary: the backend states the machine
 * fact, the client owns the sentence and the language. So this function resolves
 * a NAME and never a string a user reads — the label and the surface that renders
 * it are separate, and a reader that returned copy would be the place i18n
 * quietly stopped being the client's decision.
 */
export function getCapabilityOutage(error: unknown): CapabilityOutage | null {
  if (serverErrorCode(error) !== CAPABILITY_UNAVAILABLE) return null

  // `details` is the code's own contract, so it is parsed with the code's own
  // schema. A failed parse still yields an OUTAGE with no name — the fallback is
  // a usable value of the right type, never a blanked read that makes the caller
  // believe nothing happened.
  const details = CapabilityUnavailableDetailsSchema.safeParse(serverErrorDetails(error))
  if (!details.success) return { capability: null }

  const { capability } = details.data
  return { capability: isOutageCapabilityName(capability) ? capability : null }
}

/**
 * Classifies any thrown value into an {@link ErrorKind} and a user-ready message.
 * Pure and SSR-safe — no Sentry, no toast, no side effects. Unit-tested in isolation.
 */
export function classifyError(error: unknown): ClassifiedError {
  const status = getErrorStatus(error)
  let kind: ErrorKind

  if (isAbortError(error) || getOAuthReconnectError(error)) {
    kind = "expected"
  } else if (getCapabilityOutage(error)) {
    // ══════════════════════════════════════════════════════════════════════
    // 🔴 ABOVE THE STATUS LADDER, NOT INSIDE THE 5xx ARM.
    // ══════════════════════════════════════════════════════════════════════
    //
    // `RequiresCapabilityGuard` throws `ServiceUnavailableException`, so this
    // refusal is a 503 TODAY and `status >= 500` would claim it as `server` —
    // reported, and shown "an unexpected error occurred". Branching on the CODE
    // above the ladder means the day a gateway rewrites the status to 502, or a
    // future guard picks 403, this arm still fires. The status is a coincidence
    // that currently agrees with the contract; see {@link getCapabilityOutage},
    // which refuses to read it for the same reason.
    //
    // It sits below `expected` because an aborted request carries no response
    // body at all, so there is no envelope for this reader to find.
    kind = "capabilityUnavailable"
  } else if (status === 401 || status === 403) {
    kind = "access"
  } else if (status === 404) {
    kind = "notFound"
  } else if (status === 409) {
    kind = "conflict"
  } else if (status === 400 || status === 422 || error instanceof ZodError) {
    kind = "validation"
  } else if (status === 429) {
    kind = "rateLimited"
  } else if (status !== undefined && status >= 500) {
    kind = "server"
  } else if (isAxiosError(error) && !error.response) {
    // Request was made but no response arrived (server unreachable / CORS / DNS).
    kind = isOffline() ? "offline" : "network"
  } else {
    kind = "unknown"
  }

  return { kind, status, userMessage: deriveUserMessage(error, kind) }
}

/** Sentry attribution for a single report. */
interface ReportMeta {
  /** Where it came from — a component/hook name, or the global net's label. */
  source: string
  /** What was attempted — operation name, or the query/mutation key. */
  operation: string
  /** Additional structured context. */
  extra?: Record<string, unknown>
}

/**
 * Reports an error to Sentry exactly once and returns its classification.
 *
 * Shared by `handleError` (Layer 2) and the global React Query net (Layer 1) so the
 * dedup WeakSet is authoritative across both. Expected/offline kinds are skipped.
 * Sentry is isomorphic, so this is safe in any runtime.
 *
 * This is where the session enters: reading it is a side effect, so `classifyError`
 * stays pure and only the reporting decision depends on it. See {@link isReportable}.
 */
export function reportError(error: unknown, meta: ReportMeta): ClassifiedError {
  const classified = classifyError(error)
  const session = readSessionSignal()

  if (!isReportable(classified, session)) return classified
  if (isObject(error)) {
    if (reportedErrors.has(error)) return classified
    reportedErrors.add(error)
  }

  Sentry.captureException(error, {
    tags: {
      source: meta.source,
      operation: meta.operation,
      kind: classified.kind,
      // Makes an unwired session probe visible: a burst of `session:unknown` on an
      // `access` kind means nothing installed a reader, not that users are signed out.
      session,
      ...(classified.status !== undefined ? { status: classified.status } : {})
    },
    extra: meta.extra,
    fingerprint: ["{{ default }}", meta.source, meta.operation, classified.kind]
  })

  return classified
}

/** Shows an error toast (no-op during SSR, where there is no toast container). */
function showErrorToast(message: string): void {
  if (typeof window === "undefined") return
  toast.error(message)
}

/**
 * Handle an error from a `catch` block: classify it, report it to Sentry once, and
 * show the right UX. The single error-handling entry point for the whole frontend.
 *
 * @example Show a toast
 * try { await api.documents.create(...) }
 * catch (error) { handleError(error, { component: "DocumentsPage", operation: "create" }) }
 *
 * @example Route 403s to a custom view, no toast
 * handleError(error, {
 *   component: "ConnectGoogleSheetModal",
 *   operation: "createGoogleSheet",
 *   onAccessError: () => setView("access-error")
 * })
 *
 * @example Report only, no toast
 * handleError(error, { component: "Analytics", operation: "track", silent: true })
 */
export function handleError(error: unknown, ctx: HandleErrorContext): void {
  // OAuth reconnect (409): the connection expired — never a Sentry event. Components that
  // open the reconnect popup branch on getOAuthReconnectError() before calling handleError;
  // for every other caller, surface the backend's user-safe "please reconnect" message so
  // the failure is never silent.
  const reconnect = getOAuthReconnectError(error)
  if (reconnect) {
    if (!ctx.silent) showErrorToast(ctx.toastMessage ?? reconnect.message)
    return
  }

  const { kind } = reportError(error, {
    source: ctx.component,
    operation: ctx.operation,
    extra: ctx.extra
  })

  // Aborted/cancelled requests — no report, no UX.
  if (kind === "expected") return

  // Typed handlers own the UX and suppress the toast.
  if (kind === "access" && ctx.onAccessError) return ctx.onAccessError()
  if (kind === "notFound" && ctx.onNotFound) return ctx.onNotFound()
  if ((kind === "network" || kind === "offline") && ctx.onNetworkError) return ctx.onNetworkError()
  if (kind === "capabilityUnavailable" && ctx.onCapabilityUnavailable) {
    // Re-read rather than thread the outage through `ClassifiedError`. The reader
    // is pure over the same error, so this cannot disagree with the branch that
    // set the kind; widening `ClassifiedError` would add a field to a type
    // `isReportable` and `ErrorDisplay` also consume and neither has any use for.
    const outage = getCapabilityOutage(error)
    if (outage) return ctx.onCapabilityUnavailable(outage)
  }

  if (ctx.silent) return

  showErrorToast(ctx.toastMessage ?? classifyError(error).userMessage)
}

/** Options for the bound `useErrorHandler` callback (component is bound at the hook). */
export type BoundHandleOptions = Omit<HandleErrorContext, "component" | "operation">

/**
 * Component-scoped {@link handleError}. Binds `component` once so call sites only pass
 * the operation. Use in React components/hooks; use the bare `handleError` elsewhere.
 *
 * @example
 * const onError = useErrorHandler("WorkspacesPage")
 * try { await create(...) } catch (e) { onError(e, "createWorkspace") }
 */
export function useErrorHandler(component: string) {
  return useCallback(
    (error: unknown, operation: string, options?: BoundHandleOptions) =>
      handleError(error, { component, operation, ...options }),
    [component]
  )
}
